import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import NewsletterForm from '@/components/feature/NewsletterForm';
import JsonLd from '@/components/base/JsonLd';
import { useSeo } from '@/hooks/useSeo';
import { articles, categories, faqs, hotTrends, platformGroups, testimonials } from '@/mocks/chigua';

const rankStyle = [
  'bg-primary-500 text-background-50',
  'bg-primary-300 text-primary-900',
  'bg-accent-500 text-background-50',
];

const platformVisual: Record<string, { bubble: string; text: string; dot: string }> = {
  primary: { bubble: 'bg-primary-100 text-primary-700', text: 'text-primary-800', dot: 'bg-primary-500' },
  accent: { bubble: 'bg-accent-100 text-accent-700', text: 'text-accent-800', dot: 'bg-accent-500' },
  secondary: { bubble: 'bg-secondary-200 text-secondary-900', text: 'text-secondary-800', dot: 'bg-secondary-600' },
};

export default function Home() {
  useSeo({
    title: '今日瓜点全掌握 · 热门话题围观指南',
    description:
      '吃瓜路线为你整理今日份的瓜田指南，聚焦微博、抖音、贴吧的热门讨论焦点。这里有最全的围观攻略与吃瓜路线图，帮你快速跟上热点节奏。',
    keywords: '吃瓜路线,吃瓜路线今日瓜点,吃瓜路线热门话题,吃瓜路线围观指南',
  });

  const [openFaq, setOpenFaq] = useState<number>(0);
  const [showTop, setShowTop] = useState(false);

  useEffect(() => {
    const onScroll = () => setShowTop(window.scrollY > 600);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const faqLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((f) => ({
      '@type': 'Question',
      name: f.q,
      acceptedAnswer: { '@type': 'Answer', text: f.a },
    })),
  };

  const platformLd = {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    itemListElement: hotTrends.slice(0, 8).map((t, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: t.title,
    })),
  };

  return (
    <>
      <JsonLd data={faqLd} />
      <JsonLd data={platformLd} />
      <Navbar />
      <main>
        {/* ============ 首屏 ============ */}
        <section className="relative overflow-hidden bg-background-50">
          <div className="pointer-events-none absolute -left-24 top-10 h-72 w-72 rounded-full bg-primary-100/60 blur-2xl" aria-hidden="true"></div>
          <div className="pointer-events-none absolute right-0 top-1/3 h-64 w-64 rounded-full bg-accent-100/70 blur-2xl" aria-hidden="true"></div>
          <div className="container-x relative grid grid-cols-1 items-center gap-10 py-12 md:py-16 lg:grid-cols-[1.1fr_0.9fr]">
            <div>
              <p className="inline-flex items-center gap-2 rounded-full border border-primary-200 bg-primary-50 px-4 py-1.5 text-xs font-medium text-primary-700">
                <span className="relative flex h-2 w-2">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-primary-400 opacity-75"></span>
                  <span className="relative inline-flex h-2 w-2 rounded-full bg-primary-500"></span>
                </span>
                实时聚合微博 · 抖音 · 贴吧热点
              </p>
              <h1 className="mt-5 font-heading text-4xl leading-tight text-foreground-900 md:text-6xl">
                今日瓜点，
                <span className="text-primary-500">全掌握</span>
              </h1>
              <p className="mt-5 max-w-xl text-base leading-7 text-foreground-600 md:text-lg">
                不想再后知后觉错过热点？「吃瓜路线」为你整理最清晰的瓜田指南——从热搜由来、讨论焦点到围观攻略，
                <strong className="font-medium text-foreground-800">几分钟看懂大家到底在聊什么</strong>，让你随时接得住梗、跟得上话题。
              </p>
              <div className="mt-7 flex flex-col gap-3 sm:flex-row sm:items-center">
                <Link
                  to="/hot"
                  className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
                >
                  进入今日热榜
                  <i className="ri-arrow-right-line" aria-hidden="true"></i>
                </Link>
                <Link
                  to="/about"
                  className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full border border-background-300 bg-background-50 px-6 py-3 text-sm font-medium text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-700"
                >
                  了解吃瓜路线
                </Link>
              </div>
              <dl className="mt-8 grid max-w-md grid-cols-3 gap-4 border-t border-background-200 pt-6">
                {[
                  { v: '1200+', l: '累计话题盘点' },
                  { v: '300+', l: '围观指南' },
                  { v: '每日', l: '同步平台焦点' },
                ].map((s) => (
                  <div key={s.l}>
                    <dt className="sr-only">{s.l}</dt>
                    <dd className="font-heading text-2xl text-primary-500 md:text-3xl">{s.v}</dd>
                    <dd className="mt-1 text-xs text-foreground-500">{s.l}</dd>
                  </div>
                ))}
              </dl>
            </div>

            {/* 右侧热榜预览卡 */}
            <div className="relative">
              <div className="rounded-3xl border border-background-200 bg-background-50 p-5 shadow-[0_18px_40px_-22px_rgba(120,60,0,0.35)] md:p-6">
                <div className="flex items-center justify-between">
                  <h2 className="flex items-center gap-2 font-heading text-lg text-foreground-900">
                    <i className="ri-fire-line text-primary-500" aria-hidden="true"></i>
                    今日瓜点 Top 5
                  </h2>
                  <Link to="/hot" className="inline-flex items-center gap-1 text-xs font-medium text-primary-600 hover:text-primary-700">
                    完整榜单 <i className="ri-arrow-right-s-line" aria-hidden="true"></i>
                  </Link>
                </div>
                <ol className="mt-4 divide-y divide-background-200">
                  {hotTrends.slice(0, 5).map((t, i) => (
                    <li key={t.title} className="flex items-center gap-3 py-3">
                      <span
                        className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-lg text-xs font-bold ${
                          rankStyle[i] || 'bg-background-200 text-foreground-500'
                        }`}
                      >
                        {i + 1}
                      </span>
                      <span className="min-w-0 flex-1 truncate text-sm text-foreground-800">{t.title}</span>
                      <span className="shrink-0 rounded-md bg-secondary-100 px-1.5 py-0.5 text-[11px] text-secondary-700">{t.platform}</span>
                    </li>
                  ))}
                </ol>
                <p className="mt-3 rounded-2xl bg-secondary-100/70 px-4 py-3 text-xs leading-5 text-secondary-700">
                  <i className="ri-sparkling-line mr-1 text-accent-600" aria-hidden="true"></i>
                  热点每分钟都在变化，跟着吃瓜路线，不错过任何一颗新鲜热瓜。
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* ============ 栏目分类 ============ */}
        <section className="border-y border-background-200 bg-background-100" aria-label="围观栏目">
          <div className="container-x flex flex-col items-start gap-4 py-6 md:flex-row md:items-center md:justify-between">
            <div>
              <h2 className="font-heading text-lg text-foreground-900">从喜欢的栏目开始吃瓜</h2>
              <p className="mt-1 text-sm text-foreground-500">挑一个你关心的方向，直达相关热门话题与围观指南。</p>
            </div>
            <div className="flex flex-wrap gap-2">
              {categories.slice(1).map((c) => (
                <Link
                  key={c.key}
                  to={`/hot?cat=${c.key}`}
                  className="rounded-full bg-background-50 px-4 py-2 text-sm text-foreground-600 ring-1 ring-background-200 transition-all hover:bg-primary-500 hover:text-background-50 hover:ring-primary-500"
                >
                  {c.label}
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* ============ 围观指南精选 ============ */}
        <section className="py-12 md:py-16">
          <div className="container-x">
            <div className="flex items-end justify-between gap-4">
              <div>
                <p className="text-xs font-medium uppercase tracking-widest text-accent-600">Guides</p>
                <h2 className="mt-2 font-heading text-2xl text-foreground-900 md:text-3xl">今日精选 · 围观指南</h2>
                <p className="mt-2 max-w-xl text-sm leading-6 text-foreground-500">
                  我们把当下讨论度最高的话题，拆成一篇篇能快速读完的「吃瓜路线图」，从起因到争议点一次讲清楚。
                </p>
              </div>
              <Link to="/hot" className="hidden shrink-0 items-center gap-1 whitespace-nowrap rounded-full border border-background-300 px-5 py-2.5 text-sm font-medium text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-700 sm:inline-flex">
                查看全部指南 <i className="ri-arrow-right-line text-xs" aria-hidden="true"></i>
              </Link>
            </div>

            <div className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {articles.map((a) => (
                <article
                  key={a.id}
                  className="group flex flex-col overflow-hidden rounded-3xl border border-background-200 bg-background-50 transition-all hover:-translate-y-1 hover:border-primary-200"
                >
                  <Link to={`/post/${a.id}`} className="relative block overflow-hidden" aria-label={a.title}>
                    <div className="aspect-[3/2] w-full overflow-hidden">
                      <img
                        src={a.image}
                        alt={a.title}
                        title={`${a.title} - 吃瓜路线`}
                        className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                    </div>
                    <span className="absolute left-3 top-3 rounded-full bg-background-50/90 px-3 py-1 text-xs font-medium text-primary-700 backdrop-blur">
                      {a.categoryLabel}
                    </span>
                    {a.hot !== '今日最热' && a.hot === '热' && (
                      <span className="absolute right-3 top-3 rounded-full bg-accent-500 px-2.5 py-1 text-xs font-medium text-background-50">
                        热
                      </span>
                    )}
                  </Link>
                  <div className="flex flex-1 flex-col p-5">
                    <h3 className="font-heading text-lg leading-snug text-foreground-900 md:text-lg">
                      <Link to={`/post/${a.id}`} className="transition-colors hover:text-primary-600">
                        {a.title}
                      </Link>
                    </h3>
                    <p className="mt-2 line-clamp-2 text-sm leading-6 text-foreground-600">{a.desc}</p>
                    <div className="mt-4 flex flex-wrap gap-2">
                      {a.tags.map((tag) => (
                        <span key={tag} className="rounded-md bg-secondary-100 px-2 py-1 text-xs text-secondary-700">
                          #{tag}
                        </span>
                      ))}
                    </div>
                    <div className="mt-5 flex items-center justify-between border-t border-background-200 pt-4 text-xs text-foreground-500">
                      <span className="flex items-center gap-1.5">
                        <i className="ri-user-smile-line text-primary-400" aria-hidden="true"></i>
                        {a.author}
                      </span>
                      <span className="flex items-center gap-3">
                        <span className="inline-flex items-center gap-1">
                          <i className="ri-eye-line" aria-hidden="true"></i>
                          {a.views}
                        </span>
                        <span className="inline-flex items-center gap-1">
                          <i className="ri-time-line" aria-hidden="true"></i>
                          {a.readTime} 分钟
                        </span>
                      </span>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* ============ 平台热点对比 ============ */}
        <section className="bg-background-100 py-12 md:py-16" id="platforms">
          <div className="container-x">
            <div className="max-w-2xl">
              <p className="text-xs font-medium uppercase tracking-widest text-accent-600">Platforms</p>
              <h2 className="mt-2 font-heading text-2xl text-foreground-900 md:text-3xl">同一条瓜，不同平台怎么聊</h2>
              <p className="mt-2 text-sm leading-6 text-foreground-500">
                微博聊观点、抖音看流量、贴吧挖深度。同样的热点在不同平台，发酵的方式完全不一样。
              </p>
            </div>

            <div className="mt-8 grid grid-cols-1 gap-5 md:grid-cols-3">
              {platformGroups.map((p) => {
                const v = platformVisual[p.color];
                return (
                  <div key={p.name} className="rounded-3xl border border-background-200 bg-background-50 p-6">
                    <div className="flex items-center gap-3">
                      <span className={`flex h-11 w-11 items-center justify-center rounded-2xl ${v.bubble}`}>
                        <i className={`${p.icon} text-xl`} aria-hidden="true"></i>
                      </span>
                      <div>
                        <h3 className={`font-heading text-lg ${v.text}`}>{p.name}</h3>
                        <p className="text-xs text-foreground-500">{p.sub}</p>
                      </div>
                    </div>
                    <ul className="mt-5 space-y-3">
                      {p.items.map((it) => (
                        <li key={it.title} className="rounded-2xl bg-background-100 px-4 py-3">
                          <p className="text-sm font-medium leading-5 text-foreground-800">{it.title}</p>
                          <p className="mt-1.5 flex items-center gap-1.5 text-xs text-foreground-500">
                            <span className={`h-1.5 w-1.5 rounded-full ${v.dot}`} aria-hidden="true"></span>
                            {it.count}
                          </p>
                        </li>
                      ))}
                    </ul>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* ============ 口碑 + 关于 ============ */}
        <section className="py-12 md:py-16">
          <div className="container-x grid grid-cols-1 gap-8 lg:grid-cols-[0.95fr_1.05fr] lg:gap-12">
            <div>
              <p className="text-xs font-medium uppercase tracking-widest text-accent-600">About</p>
              <h2 className="mt-2 font-heading text-2xl text-foreground-900 md:text-3xl">围观者的口碑，是最好的口碑</h2>
              <p className="mt-3 max-w-md text-sm leading-7 text-foreground-600">
                从追剧十年的老粉，到每天赶选题的新媒体小编，「吃瓜路线」努力把「先看清、再参与」的围观方式带给更多人。
                我们不追热度抢眼球，只想把每一条瓜的来龙去脉讲明白。
              </p>
              <ul className="mt-6 space-y-4">
                {[
                  { icon: 'ri-focus-3-line', text: '按天整理平台热点，脉络清晰不啰嗦' },
                  { icon: 'ri-verified-badge-line', text: '不传未经证实的料，尊重信息边界' },
                  { icon: 'ri-heart-3-line', text: '理性围观，让吃瓜变成轻松的社交加分项' },
                ].map((f) => (
                  <li key={f.text} className="flex items-center gap-3 text-sm text-foreground-700">
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-accent-100 text-accent-700">
                      <i className={f.icon} aria-hidden="true"></i>
                    </span>
                    {f.text}
                  </li>
                ))}
              </ul>
              <Link
                to="/about"
                className="mt-7 inline-flex items-center gap-2 whitespace-nowrap rounded-full bg-foreground-950 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-foreground-800"
              >
                了解更多
                <i className="ri-arrow-right-line" aria-hidden="true"></i>
              </Link>
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              {testimonials.map((t) => (
                <figure key={t.name} className="flex flex-col rounded-3xl border border-background-200 bg-background-100 p-6">
                  <i className="ri-double-quotes-l text-2xl text-primary-300" aria-hidden="true"></i>
                  <blockquote className="mt-3 flex-1 text-sm leading-6 text-foreground-700">{t.text}</blockquote>
                  <figcaption className="mt-5 flex items-center gap-3 border-t border-background-200 pt-4">
                    <span className="flex h-10 w-10 items-center justify-center rounded-full bg-primary-500 font-heading text-sm text-background-50">
                      {t.name.slice(0, 1)}
                    </span>
                    <div>
                      <p className="text-sm font-medium text-foreground-900">{t.name}</p>
                      <p className="text-xs text-foreground-500">{t.tag}</p>
                    </div>
                  </figcaption>
                </figure>
              ))}
            </div>
          </div>
        </section>

        {/* ============ FAQ ============ */}
        <section className="bg-background-100 py-12 md:py-16">
          <div className="container-x grid grid-cols-1 gap-8 lg:grid-cols-[0.9fr_1.1fr] lg:gap-12">
            <div>
              <p className="text-xs font-medium uppercase tracking-widest text-accent-600">FAQ</p>
              <h2 className="mt-2 font-heading text-2xl text-foreground-900 md:text-3xl">关于吃瓜路线，常见问题</h2>
              <p className="mt-3 text-sm leading-7 text-foreground-600">
                对「吃瓜路线」还有疑问？先看看大家最常问的这几个问题，如果没找到答案，欢迎随时联系我们。
              </p>
              <Link
                to="/about"
                className="mt-6 inline-flex items-center gap-1 text-sm font-medium text-primary-600 hover:text-primary-700"
              >
                去关于页了解更完整的我们 <i className="ri-arrow-right-line" aria-hidden="true"></i>
              </Link>
            </div>

            <div className="space-y-3">
              {faqs.map((f, i) => {
                const open = openFaq === i;
                return (
                  <div key={f.q} className="overflow-hidden rounded-2xl border border-background-200 bg-background-50">
                    <button
                      type="button"
                      onClick={() => setOpenFaq(open ? -1 : i)}
                      className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left"
                      aria-expanded={open}
                    >
                      <h3 className="text-sm font-medium text-foreground-900 md:text-base">{f.q}</h3>
                      <span
                        className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full transition-all ${
                          open ? 'rotate-180 bg-primary-500 text-background-50' : 'bg-background-200 text-foreground-500'
                        }`}
                      >
                        <i className="ri-add-line" aria-hidden="true"></i>
                      </span>
                    </button>
                    <div className={`px-5 transition-all ${open ? 'max-h-96 pb-5' : 'max-h-0'}`}>
                      <p className="border-t border-background-200 pt-3 text-sm leading-6 text-foreground-600">{f.a}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        <NewsletterForm />
      </main>

      <Footer />

      {showTop && (
        <button
          type="button"
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          className="fixed bottom-6 right-6 z-40 flex h-11 w-11 items-center justify-center rounded-full bg-primary-500 text-background-50 shadow-lg transition-colors hover:bg-primary-600"
          aria-label="返回顶部"
        >
          <i className="ri-arrow-up-line" aria-hidden="true"></i>
        </button>
      )}
    </>
  );
}